import React, { useEffect } from "react";
import styled from "@emotion/styled";
import { Grid } from "@mui/material";
import { COLOR_MAP } from "../../constants/ColorNames";
import Sidebar from "./sideBar/Sidebar";
import Article from "./article/Article";
import BottomPanel from "./bottomPanel/BottomPanel";
import { useGetDraftDataQuery, useGetFolderNameListMutation, useGetSearchDataQuery, useLazyGetHeaderDataQuery, useLazyTrackHealthQuery } from "../../redux/services/folderName";
import HeaderPanelSkeleton from "./skeleton/header";
import SideBarSkeleton from "./skeleton/sideBar";
import BottomPanelSkeleton from "./skeleton/bottomPanel";
import ArticleSkeleton from "./skeleton/article";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { useSearchParams } from "react-router-dom";
import { setAboutCompany, setCampaignCreationQueryData, setCampaignName, setCampaignTemplate, setCandidateNo, setCompanyName, setContentPersonalisationGenratedViaAI, setContentPersonalisationType, setCurrency, setCurrentCount, setDisabledField, setFolderId, setFolderName, setJobDescription, setJobLocation, setJobTitle, setMasterData, setMaxExperience, setMaxSalary, setMinExperience, setMinSalary, setRephraseCount, setRephrasedEmailObj, setReplyId, setSelectedContentPersonalisationKey, setSendMailToContactedProfiles, setTabStates } from "../../redux/slice/campaignSlice";
import { ChannelInfo, filterObject, getChannelId, getCurrencyBasedOnSiteContext, getCurrencyFromHexCode, getHealthApiPayload, prefillFields } from "../../utils/helpers";
import { placeholderMapping } from "../../constants/helpers";
import TemporaryDrawer from "./TemporaryDrawer/TemporaryDrawer";
import { setJobPreview } from "../../redux/slice/previewSlice";
import { formatNumberString, unFormatNumberString } from "../../utils/whatsAppUtils";
import Banner from "../../common/components/Banner";
import { bannerDataSelector, inventoryUsageSelector } from "../../redux/selectors/campaignSetup.selector";
import { ToastProvider } from "@foundit/ui-library";

type LayoutProps = {
  isLoading: boolean;
  recruiterId: string;
  bannerRef: any;
}

const ParentContainer = styled(Grid)`
  display: flex;
  width: 100%;
  max-width: 144rem;
  margin: 0 auto;
  padding: 3.2rem;
  padding-top: 0rem;
  & .MuiButtonBase-root {
    text-transform: capitalize;
  }
`
const Header = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
  margin-bottom: -10px;
`
const ContentWrapper = styled.div`
  background-color: ${COLOR_MAP.WHITE_BACKGROUND};
`

const Layout = (props: LayoutProps) => {
  const [searchParams] = useSearchParams();
  const { isLoading, recruiterId, bannerRef} = props;
  const dispatch = useDispatch();
  const userData = useSelector((state: RootState) => state.campaign.userData);
  const [trackerHealthTrigger]: any = useLazyTrackHealthQuery();
  const startTime = React.useRef(new Date());
  const queryObject = Object.fromEntries(searchParams.entries());
  const hasTriggered = React.useRef(false);

  const {bannerData} = useSelector(bannerDataSelector)
  const {inventoryUsage} = useSelector(inventoryUsageSelector)
  
  const urlParams = new URLSearchParams(window.location.search);
  const campaignId = urlParams.get('campaignId');
  const searchId = urlParams.get('searchId');
  const initiationId = urlParams.get('initiationId');
  const { data: draftData } = useGetDraftDataQuery(campaignId, { skip: !campaignId });
  const { data: searchData } = useGetSearchDataQuery(initiationId, { skip: !searchId })
  const [getFolderNameList, { data: foldersListData, isLoading: isFetchingFolders, isUninitialized: folderUninitialized }] = useGetFolderNameListMutation()
  const { rephrasedEmailObj } = useSelector((state: RootState) => state.campaign.emailGeneratedState);
  const { jobPreview } = useSelector((state: any) => state.jobPreview)

  const preloadedData = draftData || searchData

  useEffect(() => {
    //setting data for draft or preloaded data
    if (filterObject(preloadedData)) {
      const { campaignDetail, campaignJobDescription, campaignStepList, campaignContentPersonalisationFilter } = draftData || {}
      const { folderId, receiveReplyOnEmail, name, isTemplateUsed, selectedCandidatesCount, sendMailToContactedProfiles } = campaignId ? (campaignDetail || {}) : (searchData || {})
      const { jobTitle, minSalary, maxSalary, minExp, maxExp, aboutCompany, companyName, jobDescription, currencyCode, disabledFields: srpDisabledFields,channelName } = campaignId ? (campaignJobDescription || {}) : (searchData || {})
      const { generatedVaiAI, type, selectedContentPersonalisationKey } = campaignContentPersonalisationFilter || {};

      //folderName
      if (folderId) {
        if (folderUninitialized) {
          getFolderNameList(null)
        } else {
          const folderSelected = foldersListData?.find((item: any) => item.folderId === folderId)
          dispatch(setFolderName({ folderName: folderSelected ? `${folderSelected?.type || ''} ${folderSelected?.name || ''} (${folderSelected?.num_profiles || 0})` : '' }));
          dispatch(setFolderId({ folderId }))
        }
      }

      // to fill the currency code from draft
      const defaultCurrency = searchData?.currency || getCurrencyFromHexCode(currencyCode) || getCurrencyBasedOnSiteContext(userData?.siteContext) || "INR";
      const currencyCodeValue = defaultCurrency || getCurrencyFromHexCode(currencyCode);
      const isIndianCurrency = currencyCodeValue === "INR";

      let minSalaryValue = "";
      let maxSalaryValue = "";

      let disabledFields = { 
         minExp: searchData?.minExp ?? srpDisabledFields?.includes('minExp') ?? false,
         maxExp: searchData?.maxExp ?? srpDisabledFields?.includes('maxExp') ?? false,
         jobLocations: searchData?.jobLocations ?? srpDisabledFields?.includes('jobLocations') ?? false
      }

      if(draftData || searchData){
         dispatch(setDisabledField(disabledFields));
               
        if(channelName){
          const { channelId } : ChannelInfo = getChannelId(channelName)
               const subChannelId = Number(channelId) >= (channelId ?? +userData?.channelId ) ? Number(userData?.subChannelId): channelId
               const masterDataQuery = {
                 channelId:channelId || userData?.channelId,
                 subChannelId: subChannelId || userData?.subChannelId,
                 recruiterId: userData?.recruiterId,
               }
          dispatch(setMasterData({masterData: masterDataQuery }))
        }
         
      }
      
      if (draftData) {
        minSalaryValue = minSalary || "";
        maxSalaryValue = maxSalary || "";
      } else if (searchData) {
        dispatch(setCurrency({currency:searchData?.currency}))
        minSalaryValue = searchData?.minSalary || "";
        maxSalaryValue = searchData?.maxSalary || "";
      }


      const isMinSalaryGreaterThanLimit = minSalaryValue && +unFormatNumberString(`${minSalaryValue}`) >= 10000000;
      const isMaxSalaryGreaterThanLimit = maxSalaryValue && +unFormatNumberString(`${maxSalaryValue}`) >= 10000000;

      minSalaryValue = formatNumberString({
        numStr: isMinSalaryGreaterThanLimit && isIndianCurrency
          ? `${"10000000"}`
          : `${minSalaryValue || ""}`,
        isIndianCurrency,
      });
      maxSalaryValue = formatNumberString({
        numStr: isMaxSalaryGreaterThanLimit && isIndianCurrency
          ? `${"10000000+"}`
          : `${maxSalaryValue || ""}`,
        isIndianCurrency,
      })


      prefillFields(dispatch, [
        { value: receiveReplyOnEmail, key: 'replyId', dispatchFunction: setReplyId },
        { value: jobTitle, key: 'jobTitle', dispatchFunction: setJobTitle },
        { value: name, key: 'campaignName', dispatchFunction: setCampaignName },
        { value: minSalaryValue, key: 'minSalary', dispatchFunction: setMinSalary },
        { value: maxSalaryValue, key: 'maxSalary', dispatchFunction: setMaxSalary },
        { value: currencyCodeValue || "", key: 'currency', dispatchFunction: setCurrency },
        { value: minExp?.toString(), key: 'minExperience', dispatchFunction: setMinExperience },
        { value: maxExp?.toString(), key: 'maxExperience', dispatchFunction: setMaxExperience },
        { value: companyName, key: 'companyName', dispatchFunction: setCompanyName },
        { value: aboutCompany, key: 'aboutCompany', dispatchFunction: setAboutCompany },
        { value: selectedCandidatesCount, key: 'candidateNo', dispatchFunction: setCandidateNo },
        { value: generatedVaiAI, key: 'contentPersonalisationGenratedViaAI', dispatchFunction: setContentPersonalisationGenratedViaAI },
        { value: type, key: 'contentPersonalisationType', dispatchFunction: setContentPersonalisationType },
        { value: selectedContentPersonalisationKey, key: 'selectedContentPersonalisationKey', dispatchFunction: setSelectedContentPersonalisationKey },
        ...(typeof sendMailToContactedProfiles !== "undefined"
          ? [{ value: Boolean(sendMailToContactedProfiles), key: 'sendMailToContactedProfiles', dispatchFunction: setSendMailToContactedProfiles }]
          : []),
        { value: jobDescription, key: 'jobDescription', dispatchFunction: setJobDescription },
      ])

      if (generatedVaiAI === 1) {
        dispatch(setRephraseCount({ rephraseCount: 1 }));
        dispatch(setCurrentCount({ currentCount: 1 }));
        const email = (campaignStepList && campaignStepList.length) ? campaignStepList[0]?.mailBody : '';
        const subject = (campaignStepList && campaignStepList.length) ? campaignStepList[0]?.mailSubject : '';
        dispatch(setRephrasedEmailObj({ rephrasedEmailObj: [...rephrasedEmailObj, { reprhaseIndex: 1, subject: subject, email: email }] }))
      }

      //preload email data on draft 
      if (campaignId) {
        //email
        if (isTemplateUsed) {
          dispatch(setCampaignTemplate({ campaignTemplate: name || '' }));
        }
        const updatedTabState = campaignStepList ? campaignStepList?.map((item: any, index: number) => {
          const label = index === 0 ? 'Email' : `Follow up-${index}`;
          let email = item.mailBody;
          let subject = item.mailSubject;

          Object.entries(placeholderMapping).forEach(([text, htmlContent]) => {
            let updatedText = text.split(':')[0];

            if (email.includes(updatedText)) {
              email = email.replace(updatedText, htmlContent);
            }
            if (subject.includes(updatedText)) {
              subject = subject.replace(updatedText, htmlContent);
            }
          });
          return {
            label: label,
            subject: subject,
            email: email,
            applyButton: item.attachApplyButton,
            replyButton: item.attachReplyButton,
            stepNumber: item.stepNumber + 1,
            selectedFollowUPdays: item.activateAfterDays,
            alreadyGenerated: Boolean(generatedVaiAI),
            alreadyRephrased: Boolean(generatedVaiAI),
          }
        }
        ) : [{
          label: 'Email',
          subject: '',
          email: '',
          applyButton: true,
          replyButton: true,
          selectedFollowUPdays: '0',
          stepNumber: 1,
        }]
        dispatch(setTabStates({ tabState: updatedTabState }));
      }
    }

  }, [draftData, searchData, foldersListData])




  React.useEffect(() => {
    if (recruiterId && !hasTriggered.current) {
      let status = recruiterId ? 200 : 400
      const healthPayload =
        getHealthApiPayload(status, queryObject, startTime.current, recruiterId, 'CCP');
      hasTriggered.current = true;
      trackerHealthTrigger(healthPayload)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recruiterId]);


  React.useEffect(() => {
    const currentParams: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      currentParams[key] = value;
    });
    
    const formattedQuery = {
      campaignInitiationId: currentParams.initiationId,
      triggeredFromPage: currentParams.triggeredFromPage,
      timeZone: currentParams.timeZone,
      isBulkCampaign: currentParams.isBulkCampaign,
      searchId: currentParams.searchId,
      childSearchId: currentParams.childSearchId
    }
    dispatch(setCampaignCreationQueryData({ campaignCreationQueryData: formattedQuery }))
  }, [dispatch, searchParams])

  const handleDrawerClose = () => {
    dispatch(setJobPreview(false))
  };

  return (
    <>
    <ToastProvider>
      <ContentWrapper>
       {!!bannerData?.[0]?.message && inventoryUsage?.isLimitReached && <Banner cta={bannerData?.[0]?.cta} bannerRef={bannerRef} styles={{
        padding: "20px 3.2rem 0px",
        boxSizing: "border-box",
        margin: "0px auto",
        maxWidth: "144rem",
       }}>
        <span ref={bannerRef}>{bannerData?.[0]?.message}</span>
        </Banner>}
        
        <ParentContainer container spacing={1}>
          <Grid item xs={3.2} sx={{ paddingLeft: '0 !important', paddingTop:"16px !important" }}>
            {isLoading ? <SideBarSkeleton /> : <Sidebar />}
          </Grid>
          <Grid item xs={8.8} sx={{ paddingLeft: '1.6rem !important',  paddingTop:"16px !important" }}>
            {isLoading ? <ArticleSkeleton /> : <Article />}
          </Grid>
          <Grid item xs={12} sx={{ paddingLeft: '0 !important', paddingTop: '1.6rem !important' }}>
            {isLoading ? <BottomPanelSkeleton /> : <BottomPanel />}
          </Grid>
        </ParentContainer>
        {jobPreview && <TemporaryDrawer
          isOpen={jobPreview}
          onClose={handleDrawerClose}
          isCloseButton={true}
        />}
      </ContentWrapper>
    </ToastProvider>
    </>
  )
}

export default React.memo(Layout);
